import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Eye, IndianRupee, MapPin, Navigation, Phone, RefreshCw, Search, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Order } from '../types';

const statusOptions = ['', 'Assigned', 'Service InProgress', 'Service Done', 'Payment Received', 'Cancelled'];

const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('en-IN') : '-';
const formatAmount = (amount?: number) => `Rs ${Number(amount || 0).toLocaleString('en-IN')}`;

/** Parse "2:30 PM" or "14:30:00" style times into a local Date. Returns null on failure. */
function parseScheduledDt(scheduledDate: string, scheduledTime: string): Date | null {
  try {
    const datePart = scheduledDate.split('T')[0];
    const [y, mo, d] = datePart.split('-').map(Number);
    const match = scheduledTime.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
    if (!match) return null;
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return new Date(y, mo - 1, d, h, m, 0, 0);
  } catch { return null; }
}

/** Returns "1h 25m left", "Delayed 30m", etc. */
function scheduleLabel(dt: Date): { text: string; delayed: boolean } {
  const diff = dt.getTime() - Date.now(); // ms
  const absMins = Math.round(Math.abs(diff) / 60000);
  const delayed = diff < 0;
  const h = Math.floor(absMins / 60);
  const m = absMins % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return {
    text: delayed ? `Delayed ${parts.join(' ')}` : `${parts.join(' ')} left`,
    delayed,
  };
}

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today'>('today');
  const pageSize = 10;

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.searchMyOrders({
        page,
        pageSize,
        searchTerm,
        status: status || undefined,
        startDate: dateFilter === 'today' ? formatDateInput(new Date()) : undefined,
        endDate: dateFilter === 'today' ? formatDateInput(new Date()) : undefined,
      });

      setOrders(response.entity?.items || []);
      setTotalCount(response.entity?.totalCount || 0);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, page, searchTerm, status]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const totals = useMemo(() => {
    return orders.reduce(
      (summary, order) => ({
        totalAmount: summary.totalAmount + Number(order.finalAmount || 0),
        serviceCost: summary.serviceCost + Number(order.totalServiceCost || 0),
      }),
      { totalAmount: 0, serviceCost: 0 }
    );
  }, [orders]);

  return (
    <div>
      <div className="page-heading">
        <div>
          <h1>My Orders</h1>
          <p>Read-only view of orders assigned to your technician mapping.</p>
        </div>
        <button
          type="button"
          className="refresh-button"
          onClick={loadOrders}
          disabled={loading}
          title="Refresh orders"
          aria-label="Refresh orders"
        >
          <RefreshCw size={16} className={loading ? 'spin-icon' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={17} />
          <input value={searchTerm} onChange={(event) => { setPage(1); setSearchTerm(event.target.value); }} placeholder="Search customer, mobile, order, device" />
        </div>
      </div>

      <div className="quick-filters">
        <button
          className={dateFilter === 'all' ? 'active' : ''}
          onClick={() => { setPage(1); setDateFilter('all'); }}
        >
          All Orders
        </button>
        <button
          className={dateFilter === 'today' ? 'active' : ''}
          onClick={() => { setPage(1); setDateFilter('today'); }}
        >
          Today's Orders
        </button>
      </div>

      <div className="status-filters" aria-label="Order status filter">
        {statusOptions.map((option) => (
          <button
            key={option || 'all'}
            className={status === option ? 'active' : ''}
            onClick={() => { setPage(1); setStatus(option); }}
          >
            {option || 'All Status'}
          </button>
        ))}
      </div>

      <div className="orders-summary-strip">
        <div className="orders-summary-card">
          <span>Orders</span>
          <strong>{totalCount}</strong>
        </div>
        <div className="orders-summary-card service">
          <span>You Earn</span>
          <strong>{formatAmount(totals.serviceCost)}</strong>
        </div>
        <div className="orders-summary-card amount">
          <span>Total Amount</span>
          <strong>{formatAmount(totals.totalAmount)}</strong>
        </div>
      </div>

      <div className="content-card table-card">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Device</th>
              <th>Status</th>
              <th className="text-center">You Earn</th>
              <th>Total Amount</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id}>
                <td><strong>{order.orderNumber}</strong></td>
                <td>
                  <div>{order.customerName}</div>
                  <small>{order.customerMobile}</small>
                </td>
                <td>
                  <div>{order.makeName}</div>
                  <small>{order.modelName}</small>
                </td>
                <td><span className="status-pill">{order.orderStatus}</span></td>
                <td className="text-center">{formatAmount(order.totalServiceCost)}</td>
                <td>{formatAmount(order.finalAmount)}</td>
                <td>{formatDate(order.orderDate)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {order.customerMobile && (
                      <a
                        className="icon-action"
                        href={`tel:${order.customerMobile}`}
                        title="Call customer"
                        onClick={e => e.stopPropagation()}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', textDecoration: 'none' }}
                      >
                        <Phone size={16} />
                      </a>
                    )}
                    <button className="icon-action" onClick={() => navigate(`/orders/${order.id}`)} title="View order">
                      <Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="empty-state">Loading orders...</div>}
        {!loading && orders.length === 0 && <div className="empty-state">No orders found.</div>}
      </div>

      <div className="mobile-order-list">
        {orders.map((order) => (
          <button className="order-card" key={order.id} onClick={() => navigate(`/orders/${order.id}`)}>
            <div className="order-card-top">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <strong>{order.orderNumber}</strong>
                {order.scheduledDate && order.scheduledTime && !['Payment Received', 'Service Done', 'Cancelled'].includes(order.orderStatus) && (() => {
                  const dt = parseScheduledDt(order.scheduledDate, order.scheduledTime);
                  if (!dt) return null;
                  const { text, delayed } = scheduleLabel(dt);
                  // Display like "2:30 PM" from stored "2:30 PM"
                  const displayTime = order.scheduledTime.replace(/:00\s*(AM|PM)$/i, ' $1').replace(/:00$/, '');
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#ede9fe', color: '#6d28d9', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        <Clock size={10} />
                        {displayTime}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', background: delayed ? '#fee2e2' : '#d1fae5', color: delayed ? '#b91c1c' : '#065f46', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                        {text}
                      </span>
                    </div>
                  );
                })()}
                <span>{formatDate(order.orderDate)}</span>
              </div>
              <span className="status-pill">{order.orderStatus}</span>
            </div>
            <div className="order-card-device">{order.makeName} {order.modelName}</div>
            <div className="order-card-customer" style={{ alignItems: 'center' }}>
              <span>{order.customerName}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }}>{order.customerMobile}</span>
                {order.customerMobile && (
                  <a
                    href={`tel:${order.customerMobile}`}
                    onClick={e => e.stopPropagation()}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#dcfce7', color: '#16a34a', borderRadius: '50%', width: 30, height: 30, flexShrink: 0, textDecoration: 'none' }}
                    title="Call customer"
                  >
                    <Phone size={15} />
                  </a>
                )}
              </div>
            </div>
            <div className="order-money-grid">
              <div className="money-tile service">
                <Wrench size={14} />
                <span>You Earn</span>
                <strong style={{ fontSize: 17 }}>{formatAmount(order.totalServiceCost)}</strong>
              </div>
              <div className="money-tile amount">
                <IndianRupee size={14} />
                <span>Total Amount</span>
                <strong style={{ fontSize: 17 }}>{formatAmount(order.finalAmount)}</strong>
              </div>
            </div>
            {order.customerAddress && (
              <div className="order-card-address">
                <MapPin size={11} style={{ flexShrink: 0, marginTop: 2, color: '#64748b' }} />
                <span style={{ flex: 1 }}>{order.customerAddress}</span>
                <a
                  className="map-btn navigate"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(order.customerAddress)}&travelmode=driving`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{ flexShrink: 0, padding: '4px 9px', fontSize: '11px' }}
                >
                  <Navigation size={11} />
                  Map
                </a>
              </div>
            )}
            <div className="order-card-footer">
              <span>View details</span>
            </div>
          </button>
        ))}
        {loading && <div className="empty-state mobile-empty">Loading orders...</div>}
        {!loading && orders.length === 0 && <div className="empty-state mobile-empty">No orders found.</div>}
      </div>

      <div className="pagination">
        <span>Showing {orders.length} of {totalCount}</span>
        <div>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
